<template>
  <div class="qf-table" ref="qfTableRef">
    <div class="qf-table-header-body" ref="qfTableHeaderBodyRef" :class="{ 'qf-bordered': bordered, 'group-table': groupInfo?.length }" @scroll="handleBodyScroll">
      <div v-if="props.hasTableHeader" class="qf-table-header-sticky" :style="{ width: `${totalTableWidth}px` }" ref="qfTableHeaderStickyRef">
        <qf-table-header ref="qfTableHeaderRef" :columns="tableColumns" :checkedAll="checkedAll" :indeterminate="indeterminate" :expandIcon="isExpandIcon" :checkedParams="checkedOptions" :rowKey="rowKey" :filterFieldArray="filterFieldArray" :showColOverlay="showColOverlay" :isGroup="groupInfo?.length" :cornerMark="cornerMark" @startResize="startResizes" @handleChangeAll="handleChangeAll" @columnResize="columnResize" @headerIconMore="headerIconMore">
          <template v-for="(_, name) in $slots" #[name]="slotProps" :key="name">
            <slot :name="name" v-bind="slotProps" />
          </template>
        </qf-table-header>
      </div>
      <div v-if="isCustomTableBody" :style="{ ...tableBodyStyle, position: 'sticky', left: 0, width: '100%' }">
        <slot name="customTableBody"></slot>
      </div>
      <div v-else class="qf-table-body" ref="qfTableBodyRef" :style="tableBodyStyle">
        <qf-table-body ref="qfTableBodyComRef" v-if="tableDataLength && !loading" :virtual="virtualParams" :columns="tableColumns" :data="tableData" :startIndex="startIndex" :loading="loading" :expandIcon="isExpandIcon" :expandedRowKeys="expandedRowKeys" :expandType="expandType" :checkedParams="checkedOptions" :rowKey="rowKey" :activeRowType="activeRowType" :activeRowKeys="activeRowKeys" :errorRowList="errorRowList" :groupInfo="groupInfo" :checkBoxClassName="checkBoxClassName" :cornerMark="cornerMark" @rowClick="handleRowClick" @handleChecked="handleChecked" @update:expandedRowKeys="$emit('update:expandedRowKeys', $event)" @expandChange="expandChange" @loadGroupData="loadGroupData" @rowDragend="emits('rowDragend', $event)">
          <template v-for="(_, name) in $slots" #[name]="slotProps" :key="name">
            <slot :name="name" v-bind="slotProps" />
          </template>
        </qf-table-body>
        <div v-else-if="loading" class="skeleton-empty">
          <qf-skeleton :tableColumns="tableColumns" :tableSkeletonRows="tableSkeletonRows" :expandIcon="isExpandIcon" :checkedParams="checkedOptions"></qf-skeleton>
        </div>
      </div>
    </div>
    <div v-if="!isCustomTableBody && tableDataLength === 0 && !loading" class="fixed-empty">
      <slot name="empty"> 暂无数据 </slot>
    </div>
    <div v-if="groupInfo?.length">
      <slot name="groupFoldOrUnfold"></slot>
    </div>
    <qf-table-footer v-if="hasPagination && groupInfo?.length === 0" :pagination="paginationObj" @pageChange="handlePageChange" @pageSizeChange="handlePageSizeChange" />
    <div v-if="isResizing" class="resize-line" :style="{ left: resizeLineLeft + 'px', height: headerBodyHeight + 'px', top: subFormResize ? '32px' : 0 }"></div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, provide, nextTick, useSlots } from 'vue';
import QfTableHeader from '../qf-table-header/qf-table-header.vue';
import QfTableBody from '../qf-table-body/qf-table-body.vue';
import QfTableFooter from '../qf-table-footer/qf-table-footer.vue';
import QfSkeleton from '../qf-skeleton/qf-skeleton.vue';
import useEventListener from '@/hooks/event-listener';
import { useThrottleFn } from '@vueuse/core';
import { rowDragDefaultData } from '../contants';
import { useVirtualList } from '../hooks/virtualList';
const { eventListener } = useEventListener();

const props = defineProps({
  columns: {
    type: Array,
    default: () => [],
    validator: (value) => {
      return Array.isArray(value) && value.length > 0;
    },
    required: true
  },
  data: {
    type: Array,
    default: () => [],
    validator: (value) => {
      return Array.isArray(value) && value.length > 0;
    },
    required: true
  },
  rowKey: {
    type: String,
    default: 'id',
    validator: (value) => {
      return typeof value === 'string' && value.trim() !== '';
    },
    required: true
  },
  pagination: { type: Object, default: () => {} },
  maxHeight: { type: Number },
  tableSkeletonRows: { type: Number, default: 10 },
  checkedList: { type: Array, default: () => [] },
  expandIcon: { type: [Boolean, String], default: false },
  expandType: { type: String, default: 'multiple' },
  activeRowType: { type: String, default: 'single' },
  expandedRowKeys: { type: Array, default: () => [] },
  activeRowKeys: { type: Array, default: () => [] },
  errorRowList: { type: Array, default: () => [] },
  filterFieldArray: { type: Array, default: () => [] },
  bordered: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  showColOverlay: { type: Boolean, default: false },
  hasPagination: { type: Boolean, default: true },
  hasTableHeader: { type: Boolean, default: true },
  checkBoxClassName: { type: String, default: '' },
  onColumnMinWidthReached: { type: Function, default: () => {} },
  groupInfo: { type: Array, default: () => [] },
  virtual: {
    type: Object
  },
  checkedParams: {
    type: Object,
    default: () => ({
      enabled: true,
      type: 'checkbox',
      width: 40,
      checked: []
    })
  },
  rowDragParams: {
    type: [Object, Boolean],
    default: rowDragDefaultData
  },
  summaryOptions: {
    type: Object,
    default: () => ({
      enabled: false,
      data: {}
    })
  },
  cornerMark: { type: Boolean, default: false }
});
console.log(123456, 'qf-table', performance.now() + 'ms', props.loading);
const emits = defineEmits(['paginationEvent', 'rowClick', 'update:checkedList', 'update:activeRowKeys', 'update:expandedRowKeys', 'columnResize', 'headerIconMore', 'expandChange', 'handelTableScroll', 'loadGroupData', 'rowDragend', 'summaryChange']);

const checkedOptions = computed(() => ({ enabled: true, type: 'checkbox', width: 40, ...props.checkedParams }));
const virtualParams = computed(() => ({ enabled: false, threshold: 40, overscan: 10, estimateHeight: 33, dynamicHeight: false, ...props.virtual }));
const rowDragParams = computed(() => ({
  ...rowDragDefaultData(),
  ...(typeof props.rowDragParams === 'boolean' ? { enabled: props.rowDragParams } : props.rowDragParams)
}));

const qfTableHeaderRef = ref(null);
const qfTableHeaderStickyRef = ref(null);
const qfTableBodyRef = ref(null); // 表格主体容器
const qfTableBodyComRef = ref(null); // 表格主体组件
const qfTableRef = ref(null);
// 选择状态
const indeterminate = ref(false);
const checkedAll = ref(false);
// 选中行的id集合
const idList = ref(props.checkedList);
// const activeRowKeysProp = ref(props.activeRowKeys);
// 表头数据
const tableColumns = computed(() => props.columns);
// 表格数据
const tableData = computed(() => props.data);
const tableDataLength = computed(() => tableData.value.length);
//表格所有默认IDs
const defaultAllIds = computed(() => {
  if (props.groupInfo?.length) return [];
  else return (tableData.value || []).map((item) => item?.[props.rowKey]) || [];
});
const summaryOptions = computed(() => ({
  ...props.summaryOptions,
  onChange: (data) => {
    emits('summaryChange', data);
  }
}));
const fixedLeftWidth = ref(0);

// Pagination
const paginationObj = ref({});
const stopPaginationWatch = watch(
  () => props.pagination,
  (v) => {
    paginationObj.value = {
      current: v?.current || 1,
      pageSize: v?.pageSize || 10,
      total: v?.total || 0,
      showPageSize: v?.showPageSize || true,
      showJumper: v?.showJumper || true,
      pageSizeOptions: v?.pageSizeOptions || [10, 20, 30, 40, 50, 100]
    };
  },
  {
    immediate: true,
    deep: true
  }
);
//滚动事件监听
const qfTableWidth = ref(0);
const windowWidth = ref(0);
const headerHeight = ref(0);
const bodyHeight = ref(0);
// 左右固定边的边框阴影
const scrollShadowLeft = ref(false);
const scrollShadowRight = ref(false);
const eventListen = () => {
  if (qfTableRef.value) {
    qfTableWidth.value = qfTableRef.value.offsetWidth;
  }
  if (qfTableBodyRef.value) {
    bodyHeight.value = qfTableBodyRef.value.getBoundingClientRect().height;
  }
};
eventListener('resize', eventListen);
// 插槽集合
const slots = useSlots();
// 是否显示展开图标
const isExpandIcon = computed(() => {
  if (!props.expandIcon) return false;
  else if (slots?.expandIcon) return 'slot';
  else return props.expandIcon;
});

const setFixedLeftWidth = () => {
  fixedLeftWidth.value = qfTableHeaderRef.value?.getFixedLeftWidth?.() || 0;
};

// 监听选中行
const stopIdListWatch = watch(
  () => idList.value,
  (v) => {
    if (props.groupInfo?.length) return;
    if (v?.length && v?.length === defaultAllIds.value?.length) {
      checkedAll.value = true;
      indeterminate.value = false;
    } else if (v?.length === 0) {
      checkedAll.value = false;
      indeterminate.value = false;
    } else {
      checkedAll.value = false;
      indeterminate.value = true;
    }
  },
  { immediate: true, deep: true }
);
// 展开行事件
const expandChange = ({ e, expandRowKeys, row }) => {
  emits('expandChange', { e, expandRowKeys, row });
};
// 全选
const handleChangeAll = (val) => {
  if (val) {
    idList.value = tableData.value?.map((item) => item?.[props.rowKey]);
  } else {
    idList.value = [];
  }
  emits('update:checkedList', idList.value);
};
// 单选
const handleChecked = (checked, id) => {
  if (checked) {
    idList.value.push(id);
  } else {
    idList.value = idList.value?.filter((item) => item != id);
  }
  emits('update:checkedList', idList.value);
};
//鼠标悬浮行
// const rowMouseover = (params) => {
//   emits('rowMouseover', params);
// };
// 鼠标移出行
// const rowMouseLeave = (params) => {
//   emits('rowMouseLeave', params);
// };
// 头部更多图标的点击事件
const headerIconMore = (params) => {
  emits('headerIconMore', params);
};
const isCustomTableBody = computed(() => {
  if (slots?.customTableBody) return true;
  return false;
});
// 列宽拖拽
const resizeData = ref(null);
const isResizing = ref(false);
const resizeLineLeft = ref(0);
const subFormResize = ref(false);
const headerBodyHeight = ref(0);
const qfTableHeaderBodyRef = ref(null);

// 达到：当列宽小于最小宽度时，只提示一次
const hasReachedMinWidth = ref(false); // 用于记录是否已经提示过
const hasReachedMaxWidth = ref(false); // 用于记录是否已经提示过
let minWidthTimer = null; // 用于存储定时器
const startResizes = (event, col, rectLeft) => {
  if (col.colKey?.split('.')?.length === 2) {
    subFormResize.value = true;
  } else {
    subFormResize.value = false;
  }
  if (qfTableHeaderBodyRef.value) {
    const tableHeight = qfTableHeaderBodyRef.value?.getBoundingClientRect()?.height;
    headerBodyHeight.value = subFormResize.value ? tableHeight - 38 : tableHeight - 6;
  }
  const tableRect = qfTableRef.value?.getBoundingClientRect();
  resizeData.value = {
    startX: event.clientX,
    resize: col.resize,
    col,
    initialWidth: col.width,
    initialLeft: rectLeft - tableRect.left
  };
  resizeLineLeft.value = resizeData.value.initialLeft + col.width;
  isResizing.value = true;
  document.body.style.cursor = 'col-resize';
  // 使用组件级别的节流函数实例
  document.addEventListener('mousemove', throttledMouseMove);
  document.addEventListener('mouseup', handleMouseUp);

  // 重置提示状态（同时处理最大和最小宽度）
  hasReachedMinWidth.value = false;
  hasReachedMaxWidth.value = false;

  // 清除之前的定时器
  if (minWidthTimer) {
    clearTimeout(minWidthTimer);
    minWidthTimer = null;
  }
};

const handleMouseMove = (event) => {
  event.preventDefault();
  if (!resizeData.value) return;

  const { initialLeft, resize, col } = resizeData.value;
  const tableRect = qfTableRef.value?.getBoundingClientRect();
  const offsetX = event.clientX - tableRect.left;

  // 计算宽度边界
  const minWidth = resize?.minWidth || 100;
  const maxWidth = resize?.maxWidth || Infinity;
  const minLimit = initialLeft + minWidth;
  const maxLimit = initialLeft + maxWidth;

  // 限制在最小和最大宽度之间
  resizeLineLeft.value = Math.min(Math.max(offsetX, minLimit), maxLimit);

  if (col.colKey === 'operations') return; // 操作列排除
  // 最小宽度处理逻辑
  if (resizeLineLeft.value <= minLimit) {
    if (!hasReachedMinWidth.value) {
      props.onColumnMinWidthReached(col);
      hasReachedMinWidth.value = true;
      minWidthTimer = setTimeout(() => {
        hasReachedMinWidth.value = false;
      }, 2000);
    }
  } else if (hasReachedMinWidth.value) {
    hasReachedMinWidth.value = false;
    clearTimeout(minWidthTimer);
    minWidthTimer = null;
  }
};
// 将节流函数提取到组件级别，确保使用同一个函数实例，必须放在 handleMouseMove 之后定义
let throttledMouseMove = useThrottleFn(handleMouseMove, 100);
const handleMouseUp = (event) => {
  if (!resizeData.value) return;
  const { startX, col, resize, initialWidth } = resizeData.value;
  const deltaX = event.clientX - startX;

  // 计算新宽度（限制在最大最小值之间）
  const minWidth = resize?.minWidth || 100;
  const maxWidth = resize?.maxWidth || Infinity;
  let newWidth = initialWidth + deltaX;

  // 操作列特殊处理（宽度计算方式不同）
  if (col.colKey === 'operations') {
    newWidth = Math.max(initialWidth - deltaX, resize?.minWidth || 100);
  } else {
    newWidth = Math.min(Math.max(newWidth, minWidth), maxWidth);
  }

  // 更新列宽
  const cck = col.colKey?.split('.');
  if (cck?.length === 2) {
    tableColumns.value?.forEach((item) => {
      if (item.colKey === cck[0]) {
        item.children?.forEach((child) => {
          if (child.colKey === col.colKey) {
            child.width = newWidth;
          }
        });
      }
    });
  } else {
    tableColumns.value?.forEach((item) => {
      if (item.colKey === col.colKey) {
        item.width = newWidth;
      }
    });
  }

  isResizing.value = false;
  resizeData.value = null;
  document.body.style.cursor = 'default';
  // 使用保存的引用正确移除事件监听器
  if (throttledMouseMove) {
    document.removeEventListener('mousemove', throttledMouseMove);
    throttledMouseMove = null;
  }
  document.removeEventListener('mouseup', handleMouseUp);
  emits('columnResize', getColumnWidths());
  nextTick(() => {
    qfTableHeaderRef.value?.handleMouseUp?.();
  });
  // 清除所有定时器
  if (minWidthTimer) {
    clearTimeout(minWidthTimer);
    minWidthTimer = null;
  }
};
// 获取字段列总宽
const getColumnWidths = () => {
  return tableColumns.value?.reduce((prev, cur) => {
    const tmp = {};
    if (cur.children?.length) {
      cur.children.forEach((item) => {
        if (item.width) {
          prev[item.colKey] = { width: item.width };
        }
      });
    } else {
      if (cur.width) tmp.width = cur.width;
      if (cur.fixed) tmp.fixed = cur.fixed;
      prev[cur.colKey] = tmp;
    }
    return prev;
  }, {});
};

// 分页事件
const handlePageChange = (page) => {
  paginationObj.value.current = page;
  emits('paginationEvent', page, paginationObj.value.pageSize);
};
// 页面大小（每页条数）改变
const handlePageSizeChange = (pageSize) => {
  paginationObj.value.pageSize = pageSize;
  emits('paginationEvent', paginationObj.value.current, pageSize);
};
// 行点击
const handleRowClick = ({ e, row }) => {
  emits('rowClick', { e, row });
  // emits('update:activeRowKeys', activeRowKeysProp.value);
  // if (props.activeRowType === 'single') {
  //   activeRowKeysProp.value = [row?.[props.rowKey]];
  // } else if (props.activeRowType === 'multiple') {
  //   activeRowKeysProp.value.push(row?.[props.rowKey]);
  // } else {
  //   activeRowKeysProp.value = [];
  // }
};
const calculateTotalWidth = (columns) => {
  return columns.reduce((acc, cur) => {
    if (cur.children) {
      cur.width = calculateTotalWidth(cur.children);
      return acc + calculateTotalWidth(cur.children);
    }
    return acc + (cur.width || 0);
  }, 0);
};
// 辅助函数
const assignRemainingWidth = (columns, remainingWidth) => {
  const flattenColumns = (cols) => {
    return cols.reduce((acc, col) => {
      if (col.children) {
        return [...acc, ...flattenColumns(col.children)];
      }
      return [...acc, col];
    }, []);
  };

  const flatColumns = flattenColumns(columns);
  if (flatColumns.length) {
    const lastColumn = flatColumns[flatColumns.length - 1];
    console.log(433123, lastColumn, remainingWidth);
    lastColumn.width = (lastColumn.width || 0) + remainingWidth - 6;
  }
  // console.log(433456, tableColumns.value, totalTableWidth.value);
};

//复选框、展开图标、拖拽手柄的宽度
const excessWidth = computed(() => (props.cornerMark ? 10 : 0) + (checkedOptions.value.enabled ? checkedOptions.value.width : 0) + (isExpandIcon.value !== false ? 20 : 0) + (rowDragParams.value.enabled ? 20 : 0) + (props.groupInfo?.length ? props.groupInfo?.length - 1 : 0) * 12);
// 计算所有左固定的列宽度总和(包含复选框和展开图标)
// const fixedLeftWidth = computed(() => {
//   // 直接遍历顶层列，累加左固定列的宽度
//   const fixedColumnsWidth = tableColumns.value.reduce((sum, column) => {
//     // 只有没有子列且fixed为'left'的列才计入
//     if (!column.children && column.fixed === 'left') {
//       return sum + (column.width || 0);
//     }
//     return sum;
//   }, 0);
//   // 返回总和
//   return excessWidth.value + fixedColumnsWidth;
// });
// 表头字段宽度计算
const totalTableWidth = computed(() => {
  if (!qfTableWidth.value) return;
  const fieldAllWidth = calculateTotalWidth(tableColumns.value) + excessWidth.value;
  console.log(514, excessWidth.value, fieldAllWidth, qfTableWidth.value);
  if (fieldAllWidth > qfTableWidth.value) {
    // 如果字段列总宽大于表格宽度，则内容宽度为字段列总宽（会出现滚动条）
    return fieldAllWidth;
  } else {
    // 否则，内容宽度为表格宽度，将剩余的宽度分配给最后一个字段列（无滚动条）
    const remainingWidth = qfTableWidth.value - fieldAllWidth;
    if (remainingWidth <= 0) return;
    assignRemainingWidth(tableColumns.value, remainingWidth);
    return qfTableWidth.value - 6;
  }
});
// 表体样式
const tableBodyStyle = computed(() => {
  const style = {
    minWidth: 0,
    width: `${totalTableWidth.value}px`,
    willChange: virtualParams.value?.enabled ? 'transform' : undefined
  };
  if (!tableDataLength.value && props.tableSkeletonRows < 10) {
    style.minHeight = '120px';
  } else if (tableDataLength.value) {
    style.minHeight = '0';
  } else {
    style.minHeight = '300px';
  }
  if (props.maxHeight) {
    const roundHeight = Math.ceil(props.maxHeight - headerHeight.value);
    style.height = `${roundHeight}px`;
  }
  return style;
});
// console.log(229, '表体高度总和', tableBodyStyle.value?.height);
// 表头和表体在X轴上的同步滚动
const handleBodyScroll = (event) => {
  const et = event.target;
  requestAnimationFrame(() => {
    scrollShadowLeft.value = et.scrollLeft > 0;
    scrollShadowRight.value = et.scrollLeft + et.clientWidth < et.scrollWidth - 1;
  });
  emits('handelTableScroll', et);
  if (virtualParams.value) {
    updateVisibleRange(et.scrollTop, et.clientHeight);
  }
};
// 监听窗口大小变化(提供方法)
const tableScrollEvent = (params) => {
  console.log(1234, '触发滚动事件了', params);
  // 参数可以是对象 {x, y}
  let x = 0,
    y = 0;
  if (params.x !== undefined || params.y !== undefined) {
    x = params.x || 0;
    y = params.y || 0;
  }
  if (!qfTableHeaderBodyRef.value) return;

  // 计算新的滚动位置
  const scrollLeft = qfTableHeaderBodyRef.value.scrollLeft;
  const scrollTop = qfTableHeaderBodyRef.value.scrollTop;
  const scrollWidth = qfTableHeaderBodyRef.value.scrollWidth;
  const clientWidth = qfTableHeaderBodyRef.value.clientWidth;
  const scrollHeight = qfTableHeaderBodyRef.value.scrollHeight;
  const clientHeight = qfTableHeaderBodyRef.value.clientHeight;

  let newScrollLeft = scrollLeft;
  let newScrollTop = scrollTop;

  // 处理水平滚动
  if (x !== 0) {
    newScrollLeft = x > 0 ? Math.min(scrollWidth - clientWidth, scrollLeft + Math.abs(x)) : Math.max(0, scrollLeft - Math.abs(x));
  }

  // 处理垂直滚动
  if (y !== 0) {
    newScrollTop = y > 0 ? Math.min(scrollHeight - clientHeight, scrollTop + Math.abs(y)) : Math.max(0, scrollTop - Math.abs(y));
  }

  // 只有当位置发生变化时才执行滚动
  if (newScrollLeft !== scrollLeft || newScrollTop !== scrollTop) {
    qfTableHeaderBodyRef.value.scrollTo({
      left: newScrollLeft,
      top: newScrollTop,
      behavior: 'smooth'
    });
  }
};
// 滚动条回到顶部
const scrollToTop = (params) => {
  console.log(566, params, qfTableHeaderBodyRef.value);
  const distance = params?.distance || 0;
  const scrollTop = qfTableHeaderBodyRef.value.scrollTop;
  const scrollHeight = qfTableHeaderBodyRef.value.scrollHeight;
  const clientHeight = qfTableHeaderBodyRef.value.clientHeight;
  let newScrollTop = scrollTop;
  if (distance !== 0) {
    newScrollTop = distance > 0 ? Math.min(scrollHeight - clientHeight, scrollTop + Math.abs(distance)) : Math.max(0, scrollTop - Math.abs(distance));
  } else {
    newScrollTop = 0;
  }
  qfTableHeaderBodyRef.value?.scrollTo({
    top: newScrollTop
    // behavior: 'smooth'
  });
};
// 刷新行数据
const refreshRow = (rk) => {
  qfTableBodyComRef.value?.refreshRow(rk);
};
// 提供修改行数据的z-index方法
const updateZIndex = ({ rowKey, zIndex }) => {
  if (!qfTableBodyComRef.value) return;
  qfTableBodyComRef.value?.updateZIndex({ rowKey, zIndex });
};
// 分组默认加载数据事件，当最后一层级的分组出现在视口内时，会触发该事件，一般用于调用数据接口
const loadGroupData = (params) => {
  emits('loadGroupData', params);
};

/***********************虚拟列表相关 S******************************** */
// 初始化虚拟列表钩子（根据 props 动态决定是否启用）
const {
  visibleRows,
  updateVisibleRange,
  measureRowHeight,
  getRowHeight,
  findRowByKey,
  getRowStyle, // 行样式计算方法
  setRowRef, // 行元素引用设置方法
  allHeight
} = useVirtualList({
  rowKey: props.rowKey,
  virtual: virtualParams.value,
  tableData: tableData
});
watch(
  () => tableData.value?.length,
  (v) => {
    if (v > 0) {
      // 数据加载后，手动触发一次虚拟列表更新
      nextTick(() => {
        if (qfTableHeaderBodyRef.value) {
          const scrollTop = qfTableHeaderBodyRef.value.scrollTop;
          const clientHeight = qfTableHeaderBodyRef.value.clientHeight;
          updateVisibleRange(scrollTop, clientHeight);
        }
        setFixedLeftWidth();
      });
    }
  }
);
provide('virtualList', {
  visibleRows: computed(() => visibleRows.value), // 如果需要的话
  getRowStyle,
  setRowRef,
  measureRowHeight,
  getRowHeight,
  findRowByKey,
  allHeight
});
/***********************虚拟列表相关 E******************************** */
onMounted(async () => {
  windowWidth.value = window.innerWidth;
  if (qfTableHeaderStickyRef.value) {
    const rowHeight = qfTableHeaderStickyRef.value?.getBoundingClientRect()?.height;
    headerHeight.value = Math.floor(rowHeight);
  }
  // 初始化判断X轴有滚动条时scrollShadowRight为true
  if (qfTableBodyRef.value) {
    scrollShadowRight.value = qfTableBodyRef.value?.scrollWidth > qfTableBodyRef.value?.clientWidth;
  }
  await nextTick();
  eventListen();
  setFixedLeftWidth();
});
onBeforeUnmount(() => {
  // 确保停止拖拽操作并清理相关资源
  handleMouseUp();

  // 兜底清理：确保移除所有可能的事件监听器
  document.removeEventListener('mousemove', throttledMouseMove);
  document.removeEventListener('mouseup', handleMouseUp);

  // 清理定时器
  if (minWidthTimer) {
    clearTimeout(minWidthTimer);
    minWidthTimer = null;
  }

  // 清理DOM引用
  qfTableRef.value = null;
  qfTableBodyRef.value = null;
  qfTableHeaderBodyRef.value = null;
  qfTableHeaderStickyRef.value = null;

  // 清理数据
  tableColumns.value = [];
  tableData.value = [];

  // 停止监听器
  stopPaginationWatch();
  stopIdListWatch();
});
provide('qfTable', {
  idList: computed(() => idList.value),
  windowWidth: computed(() => windowWidth.value),
  qfTableWidth: computed(() => qfTableWidth.value - 6),
  scrollShadowLeft: computed(() => scrollShadowLeft.value),
  scrollShadowRight: computed(() => scrollShadowRight.value),
  qfTableHeaderBodyRef: computed(() => qfTableHeaderBodyRef.value),
  rowDragParams: computed(() => rowDragParams.value),
  qfTableHeaderStickyRef: computed(() => qfTableHeaderStickyRef.value),
  qfTableBodyRef: computed(() => qfTableBodyRef.value),
  qfTableRef: computed(() => qfTableRef.value),
  summaryOptions: computed(() => summaryOptions.value),
  fixedLeftWidth: computed(() => fixedLeftWidth.value),
  bodyHeight: computed(() => bodyHeight.value)
});
// 抛出方法
defineExpose({
  scrollToTop,
  refreshRow,
  tableScrollEvent,
  qfTableHeaderStickyRef,
  qfTableBodyRef,
  updateZIndex,
  qfTableRef,
  qfTableScrollContainerRef: qfTableHeaderBodyRef
});
</script>

<style scoped lang="less">
@import './qf-table.less';
</style>
